import React, { useContext, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Phone, MessageCircle, Clock, Baby, GripVertical, User, Calendar, MapPin, AlertCircle, FileText, CheckCircle2, XCircle, RotateCcw, ExternalLink } from 'lucide-react';
import NumberFormat from 'react-number-format';
import { LanguageContext, CenterContext, ClassesContext } from '../../../../Context';
import Staff from '../../components/Staff';

/**
 * LeadCard - A draggable card representing a single lead in the pipeline.
 * Phone-centric display with formatted phone, stage-colored left accent,
 * progressive stage-specific content, and bilingual labels.
 *
 * @param {Object} props
 * @param {Object} props.lead - The lead data object
 * @param {Function} props.onClick - Callback when card is clicked to open profile
 * @param {Object} props.stage - Stage config for accent color
 * @returns {JSX.Element}
 * @author Forge, opencode glm-5.1
 * @version 3.1
 * @since 2026-04-16
 */

const STATUS_CONFIG = {
  scheduled: { icon: Calendar, label: 'Scheduled', color: '#3b82f6', bg: '#eff6ff' },
  completed: { icon: CheckCircle2, label: 'Completed', color: '#10b981', bg: '#ecfdf5' },
  'no-show': { icon: XCircle, label: 'No-show', color: '#ef4444', bg: '#fef2f2' },
  rescheduled: { icon: RotateCcw, label: 'Rescheduled', color: '#f59e0b', bg: '#fffbeb' },
};

const toZaloLink = (phone) => `https://zalo.me/${phone.replace(/^0/, '84')}`;

export default function LeadCard({ lead, onClick, stage }) {
  const { l, language } = useContext(LanguageContext);
  const { getLocation } = useContext(CenterContext);
  const { classes } = useContext(ClassesContext);

  const t = (key, fallback) => {
    try { return l[key]?.[language] || fallback || key; } catch { return fallback || key; }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id || lead._id, data: { lead } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const priorityColors = {
    high: '#dc2626',
    medium: '#d97706',
    low: '#16a34a',
  };

  const priorityDot = priorityColors[lead.priority] || priorityColors.medium;

  const lastContact = lead.contacts?.length > 0
    ? lead.contacts[lead.contacts.length - 1]
    : null;

  const lastContactedBy = lastContact?.contactedBy || null;

  const daysSinceContact = lastContact
    ? Math.floor((Date.now() - new Date(lastContact.date).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const contactColor = daysSinceContact === null
    ? '#94a3b8'
    : daysSinceContact <= 1
      ? '#10b981'
      : daysSinceContact <= 3
        ? '#f59e0b'
        : '#ef4444';

  const showFooter = lead.addedBy || lead.children?.length > 0 || lastContact || lastContactedBy;

  const accentColor = stage?.color || '#64748b';

  const locationName = useMemo(() => {
    if (!lead.testLocation) return '';
    if (lead.testLocation.startsWith('loc_') || lead.testLocation.length > 10) {
      const loc = getLocation?.(lead.testLocation);
      return loc?.name || lead.testLocation;
    }
    return lead.testLocation;
  }, [lead.testLocation, getLocation]);

  const testDateInfo = useMemo(() => {
    if (!lead.testDate) return null;
    const d = new Date(lead.testDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.floor((d - today) / (1000 * 60 * 60 * 24));
    return { date: lead.testDate, time: lead.testTime || '', isOverdue: diff < 0, isToday: diff === 0, isTomorrow: diff === 1, diff };
  }, [lead.testDate, lead.testTime]);

  const statusCfg = STATUS_CONFIG[lead.testStatus];

  const placementName = useMemo(() => {
    if (!lead.placementResult) return '';
    if (lead.placementResult.length > 10 || lead.placementResult.startsWith('loc_')) {
      const cls = classes?.find((c) => c._id === lead.placementResult);
      if (cls) return cls.name;
    }
    return lead.placementResult;
  }, [lead.placementResult, classes]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-[10px] border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-all duration-150 cursor-pointer group"
      onClick={() => onClick(lead)}
    >
      <div className="flex">
        <div
          className="w-[3px] rounded-l-[10px] flex-shrink-0"
          style={{ backgroundColor: accentColor }}
        />

        <div className="flex-1 p-3">
          <div className="flex items-start gap-2">
            <button
              className="mt-1 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical size={14} />
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <Phone size={11} className="text-slate-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-slate-800">
                  {lead.phone ? (
                    <NumberFormat displayType="text" format="###-###-####" value={lead.phone} />
                  ) : (
                    <span className="text-slate-300 italic">{t('pipeline.noPhone', 'No phone')}</span>
                  )}
                </span>
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: priorityDot }}
                  title={`${t('pipeline.priority', 'Priority')}: ${t(`pipeline.${lead.priority}`, lead.priority)}`}
                />
              </div>

              {lead.name ? (
                <p className="text-xs text-slate-500 truncate ml-[19px] mb-0.5">{lead.name}</p>
              ) : (
                <p className="text-[11px] text-slate-300 truncate ml-[19px] mb-0.5 italic">{t('pipeline.noName', 'No name')}</p>
              )}

              {/* Stage-specific progressive content */}
              {lead.stage === 'add-zalo' && (
                <div className="ml-[19px] mt-1.5 space-y-1.5">
                  {lead.zalo ? (
                    <a
                      href={toZaloLink(lead.zalo)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold text-white transition-colors duration-150 cursor-pointer"
                      style={{ backgroundColor: '#0077CC' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0066aa'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0077CC'; }}
                    >
                      <MessageCircle size={13} />
                      {t('pipeline.openInZalo', 'Open in Zalo')}
                      <ExternalLink size={11} />
                    </a>
                  ) : (
                    <span className="text-[10px] italic text-blue-400">
                      {t('pipeline.addZaloNumber', 'Add Zalo number')}
                    </span>
                  )}
                  {lead.source && (
                    <div>
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        {t(`pipeline.${lead.source}`, lead.source)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {lead.stage === 'lead' && (
                <>
                  {lead.zalo && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 ml-[19px]">
                      <MessageCircle size={10} className="flex-shrink-0" />
                      <span className="truncate">{lead.zalo}</span>
                    </div>
                  )}
                  {lead.source && (
                    <div className="ml-[19px] mt-0.5">
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        {t(`pipeline.${lead.source}`, lead.source)}
                      </span>
                    </div>
                  )}
                </>
              )}

              {lead.stage === 'book-test' && (
                <div className="ml-[19px] mt-1 space-y-1">
                  {testDateInfo ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                        testDateInfo.isOverdue ? 'bg-red-50 text-red-700' :
                        testDateInfo.isToday ? 'bg-amber-50 text-amber-700' :
                        'bg-blue-50 text-blue-700'
                      }`}>
                        <Calendar size={9} />
                        {lead.testTime ? `${testDateInfo.date} ${lead.testTime}` : testDateInfo.date}
                      </span>
                      {testDateInfo.isOverdue && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                          <AlertCircle size={8} />
                          {t('pipeline.overdue', 'Overdue')}
                        </span>
                      )}
                      {testDateInfo.isToday && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          {t('pipeline.today', 'Today')}
                        </span>
                      )}
                      {testDateInfo.isTomorrow && (
                        <span className="text-[10px] text-slate-400">
                          {t('pipeline.tomorrow', 'Tomorrow')}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-[10px] italic text-amber-500">
                      {t('pipeline.scheduleTest', 'Schedule test →')}
                    </span>
                  )}
                  {locationName && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                      <MapPin size={9} className="flex-shrink-0" />
                      <span>{locationName}</span>
                    </div>
                  )}
                </div>
              )}

              {lead.stage === 'test' && (
                <div className="ml-[19px] mt-1 space-y-1">
                  {testDateInfo && (
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
                        testDateInfo.isToday ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                      }`}>
                        <Calendar size={9} />
                        {testDateInfo.isToday ? t('pipeline.today', 'Today') : testDateInfo.date}
                        {lead.testTime ? ` ${lead.testTime}` : ''}
                      </span>
                    </div>
                  )}
                  {statusCfg && (
                    <div className="flex items-center gap-1" style={{ color: statusCfg.color }}>
                      <statusCfg.icon size={10} />
                      <span className="text-[11px] font-semibold">{t(`pipeline.${lead.testStatus}`, statusCfg.label)}</span>
                    </div>
                  )}
                  {placementName && (
                    <div className="flex items-center gap-1 text-[11px]">
                      <FileText size={9} className="text-emerald-500" />
                      <span className="font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">{placementName}</span>
                    </div>
                  )}
                  {locationName && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-400">
                      <MapPin size={9} className="flex-shrink-0" />
                      <span>{locationName}</span>
                    </div>
                  )}
                </div>
              )}

              {lead.stage === 'enrol' && (
                <div className="ml-[19px] mt-1 space-y-1">
                  {placementName && (
                    <div className="flex items-center gap-1">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 size={9} />
                        {placementName}
                      </span>
                    </div>
                  )}
                  {lead.source && (
                    <div>
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        {t(`pipeline.${lead.source}`, lead.source)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {showFooter && (
                <div className="flex items-center gap-3 mt-2 pt-1.5 border-t border-slate-100 ml-[19px]">
                  {lead.addedBy && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-400">
                      <User size={10} />
                      <span className="truncate max-w-[60px]"><Staff id={lead.addedBy} /></span>
                    </div>
                  )}
                  {lead.children?.length > 0 && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-400">
                      <Baby size={10} />
                      <span>{lead.children.length}</span>
                    </div>
                  )}
                  {lastContact && (
                    <div className="flex items-center gap-1 text-[11px]" style={{ color: contactColor }}>
                      <Clock size={10} />
                      <span>
                        {daysSinceContact === 0
                          ? t('pipeline.today', 'Today')
                          : `${daysSinceContact}${t('pipeline.dayAgo', 'd')}`
                        }
                      </span>
                    </div>
                  )}
                  {lastContactedBy && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-400 ml-auto">
                      <User size={9} />
                      <span className="truncate max-w-[50px]"><Staff id={lastContactedBy} /></span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}